/* eslint-disable jsx-a11y/anchor-is-valid */
import { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation, Trans } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { stringify } from 'query-string';
import clsx from 'clsx';

import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Link from '@mui/material/Link';

import IconButton from '@design-system/IconButton';
import Tooltip from '@design-system/Tooltip';
import ChevronDown from '@experimental-components/IconsComponents/ChevronDown';
import ButtonPlus from '@experimental-components/ButtonPlus';
import CustomDropdown from '@experimental-components/CustomDropdown';

import { useMenuScopeIdentifier } from 'hooks/menuScope';

import { drawerActions, drawerEntities, getQueryParamsMenuMaker } from 'utils/drawerMenuMaker';
import { typesAddingProducts } from 'utils/products';
import { MENU_SCOPE } from 'utils/menuMaker/constants';

import RowProduct from './RowProduct';
import HelperSkeleton from './HelperSkeleton';
import useStyles from './styles';

/**
 * RowCategory Component
 * Renders a category row with expandable content showing products
 * Includes functionality to add new products with scope-based restrictions
 *
 * @param {Object} props - Component props
 * @param {boolean} [props.openDefault=false] - Whether the row should be open by default
 * @param {Object} props.category - Category data object
 * @param {Object} props.menu - Menu data object
 * @param {boolean} props.hasProductOverrides - Whether the menu has product overrides
 * @param {Function} props.onLoadDetails - Function to load category details
 * @param {boolean} props.openDrawer - Whether a drawer is currently open
 * @returns {React.ReactElement} Category row component
 */
function RowCategory({ openDefault = false, category, menu, hasProductOverrides, onLoadDetails, openDrawer }) {
  const classes = useStyles();
  const { t } = useTranslation('menuMaker');
  const [openRow, setOpenRow] = useState(openDefault);
  const history = useHistory();
  const location = useLocation();
  const { isStoreMode } = useMenuScopeIdentifier();

  const { entity } = getQueryParamsMenuMaker(location);

  const hideOverrideColumn = (() => {
    const entityCheck = entity === drawerEntities.PRODUCT_OVERRIDE;
    return (openDrawer && entityCheck) ?? false;
  })();

  // Show "Add Product" functionality only when NOT in restrictive store mode
  const shouldShowAddProduct = !isStoreMode;

  const handleLoadDetails = () => {
    setOpenRow((state) => !state);
    if (!category?.isLoaded) {
      onLoadDetails(menu?.uuid, category?.uuid);
    }
  };

  const handleEditCategory = (categoryUuid) => {
    const query = { action: drawerActions.EDIT, entity: drawerEntities.CATEGORY, uuid: categoryUuid };

    history.replace({ search: stringify(query) });
  };

  const handleOpenCreateProduct = (value) => {
    let query;

    if (value === typesAddingProducts.NEW) {
      query = {
        action: drawerActions.CREATE,
        entity: drawerEntities.PRODUCT,
        categoryOfProduct: category?.uuid,
        menuOfProduct: menu?.uuid,
      };
      history.replace({ search: stringify(query) });
    }

    if (value === typesAddingProducts.EXISTING) {
      query = {
        action: drawerActions.EDIT,
        entity: drawerEntities.CATEGORY,
        uuid: category?.uuid,
        focusInProducts: true,
      };
      history.replace({ search: stringify(query) });
    }
  };

  function renderContentCategory() {
    if (category?.isLoading) {
      return <HelperSkeleton type="contentCategory" />;
    }

    return category?.products?.map((product) => (
      <RowProduct
        hasProductOverrides={hasProductOverrides}
        key={`${menu?.uuid}-${product?.uuid}`}
        menu={menu}
        openDrawer={openDrawer}
        product={product}
        t={t}
      />
    ));
  }

  return (
    <Box>
      <Box className={clsx(classes.headerRow, classes.rowBorderBottom)}>
        <Link
          className={classes.labelCategoryRow}
          component="button"
          data-testid={category?.uuid}
          onClick={() => handleEditCategory(category?.uuid)}
          underline="none"
          variant="body2"
        >
          {category?.name}
        </Link>

        <Box className={classes.contentLeftRow}>
          <Box className={clsx(classes.labelHeaderColumns, classes.columnPrice)}>
            {t('menuMaker:contentResume.labels.price')}
          </Box>

          {hasProductOverrides && !hideOverrideColumn && (
            <Box className={clsx(classes.labelHeaderColumns, classes.columnPriceInMenu)}>
              {t('menuMaker:contentResume.labels.priceInMenu')}
              <Tooltip title={<Trans i18nKey="menuMaker:contentResume.labels.priceInMenuTooltip" />}>
                <Tooltip.Help size={34} />
              </Tooltip>
            </Box>
          )}

          {hasProductOverrides && !openDrawer && (
            <Box className={clsx(classes.labelHeaderColumns, classes.columnProduct)}>
              {t('menuMaker:contentResume.labels.includeInMenu')}
            </Box>
          )}

          <IconButton aria-label="collapse-category-row" onClick={() => handleLoadDetails()} size="large">
            <ChevronDown
              className={clsx(classes.iconInitialState, { [classes.iconRotate]: openRow })}
              color="#000"
              lighter
              size={16}
            />
          </IconButton>
        </Box>
      </Box>
      <Collapse in={openRow}>
        {renderContentCategory()}
        <Box px={4} py={3}>
          {shouldShowAddProduct && (
            <CustomDropdown
              onChange={handleOpenCreateProduct}
              renderButton={(openOptions) => (
                <ButtonPlus data-testid={`btnAddProductOfCategory_${category?.uuid}`} onClick={openOptions}>
                  {t('menuMaker:buttons.addProduct')}
                </ButtonPlus>
              )}
            >
              <CustomDropdown.Option value={typesAddingProducts.EXISTING}>
                {t('menuMaker:buttons.addExistingProduct')}
              </CustomDropdown.Option>

              <CustomDropdown.Option value={typesAddingProducts.NEW}>
                {t('menuMaker:buttons.addNewProduct')}
              </CustomDropdown.Option>
            </CustomDropdown>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}
RowCategory.propTypes = {
  category: PropTypes.object,
  hasProductOverrides: PropTypes.bool,
  onLoadDetails: PropTypes.func,
  menu: PropTypes.object,
  openDefault: PropTypes.bool,
  openDrawer: PropTypes.bool,
};

export default RowCategory;
